import { useCallback, useEffect, useRef } from "react";
import { DebugLogger } from "../../../domains/app/utils/debugLogger";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../../tools/analytics/common.analytics";
import {
  gaEventArticleConsumed,
  gaEventArticleViewOriginalPaper,
  gaEventViewItemArticle,
} from "../../../tools/analytics/domains/article.analytics";
import {
  getContentLocation,
  getContentOrigin,
  trackArticleSectionRead,
  trackContentViewDuration,
} from "../../../tools/analytics/base.analytics";
import {
  observeDwell,
  useContentProgress,
} from "../../../tools/analytics/useContentProgress";
import { apiClient, type Api } from "../../../services/api";
import { useFirebaseUser } from "../../../domains/user/hooks/useFirebaseUser";
import { recordArticleRead } from "../../../domains/article/utils/articleReads";
import type { ArticleDetail } from "./useArticleData";
import { useCongressAttributionFromNavigation } from "../../../domains/congress/congressAttribution";

const debug = new DebugLogger("Article Analytics Hook");

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];
type ArticleAnalyticsModel = ArticleDetail | ArticleListItem;

const CONSUMPTION_TIMER_MS = 10_000;

// The bottom of an article sits under the chat bar's reserved space: 97% of
// the page is the whole text.
const COMPLETE_SCROLL_RATIO = 0.97;

// The attribute every article section carries, on both surfaces. The sections
// a reading reports are read from the DOM, not from a list kept here.
const SECTION_ATTRIBUTE = "data-gtm-section";

/**
 * Closest element with `overflow-y: auto | scroll`, starting at `el` ITSELF.
 *
 * Starting at `el.parentElement` skipped the anchor, and callers legitimately
 * pass the scroll container as the anchor: DesktopArticlePage hands us the very
 * `<div className="flex-1 overflow-y-auto">` it scrolls. The walk then found no
 * scrolling ancestor, returned null, and the caller fell back to `window` —
 * which never scrolls in that layout, so no scroll ever marked an article read.
 * Desktop was left with the 10s timer alone and fired `article_consumed` on
 * 6.5% of article opens, against 19.8% on mobile, whose anchor is an inner div
 * and so walked correctly.
 */
export function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node !== null) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

export function useArticleAnalytics(
  article: ArticleAnalyticsModel | null,
  isArticleReadMarkerOnScreen: boolean,
  currentRoom: PrivateRoom | null,
  scrollAnchorRef: React.RefObject<HTMLElement | null>
) {
  const { isAuthenticatedFirebase } = useFirebaseUser();
  // Stamped by the card that navigated here; null on every other entry. Read
  // in the hook rather than in the pages so ArticlePage and DesktopArticlePage
  // cannot drift apart on it.
  const congressAttribution = useCongressAttributionFromNavigation();
  const memSlugRef = useRef<string>("");
  const isConsumedRef = useRef(false);
  const isArticleReadRef = useRef(false);
  const isSourceViewedRef = useRef(false);

  const viewStartRef = useRef<number | null>(null);
  const viewedArticleRef = useRef<ArticleAnalyticsModel | null>(null);
  const viewLocationRef = useRef<"public app" | "room" | "profile">(
    "public app"
  );
  const viewOriginRef = useRef<string>("public app");

  const articleRef = useRef(article);
  articleRef.current = article;

  // Effect dependencies, kept as explicit nulls. `article?.id` would put
  // `undefined` in a dependency array, and undefined is not a value this
  // codebase carries across a boundary.
  const articleId = article === null ? null : article.id;
  const articleSlug = article === null ? null : article.slug;

  const isAuthenticatedRef = useRef(isAuthenticatedFirebase);
  isAuthenticatedRef.current = isAuthenticatedFirebase;

  // The reading of one open: depth, sections and tabs. The scroll element is
  // captured at the start, since the anchor ref is gone by the time an
  // unmount ends the open.
  const scrollElementRef = useRef<HTMLElement | null>(null);
  const sectionsRef = useRef<ReturnType<typeof observeDwell> | null>(null);
  const maxScrollPxRef = useRef(0);
  // The height of this article's page at its last measure: by the time a
  // swipe ends the open, the element holds the next article.
  const pageHeightPxRef = useRef(0);
  const tabsVisitedRef = useRef<string[]>([]);

  function measureScroll() {
    const element = scrollElementRef.current;
    if (element === null) return;
    const bottom = element.scrollTop + element.clientHeight;
    if (bottom > maxScrollPxRef.current) maxScrollPxRef.current = bottom;
    pageHeightPxRef.current = element.scrollHeight;
    const ratio =
      element.scrollHeight === 0 ? 0 : bottom / element.scrollHeight;
    progress.reportPercent(ratio >= COMPLETE_SCROLL_RATIO ? 100 : ratio * 100);
  }

  const progress = useContentProgress({
    subject:
      article === null
        ? null
        : { contentType: "article", contentId: article.id },
    endOnHidden: true,
    onStart: () => {
      const anchor = scrollAnchorRef.current;
      const container = anchor === null ? null : findScrollContainer(anchor);
      const element = container === null ? document.documentElement : container;
      scrollElementRef.current = element;
      maxScrollPxRef.current = 0;
      pageHeightPxRef.current = element.scrollHeight;
      tabsVisitedRef.current = [];
      if (sectionsRef.current !== null) sectionsRef.current.disconnect();
      sectionsRef.current = observeDwell(element, SECTION_ATTRIBUTE, () => {});
    },
    onSettled: measureScroll,
    onFinish: (open) => {
      const sections = sectionsRef.current;
      if (sections === null) {
        throw new Error("should not happen: an article open without sections");
      }
      const snapshot = sections.snapshot();
      sections.disconnect();
      sectionsRef.current = null;
      for (const section of snapshot) {
        trackArticleSectionRead(open, {
          section: section.key,
          sectionOrder: section.order,
          seconds: section.seconds,
          reached: section.reached,
        });
      }
      return {
        mediaDurationSeconds: null,
        playlistItems: null,
        article: {
          maxScrollPx: Math.round(maxScrollPxRef.current),
          pageHeightPx: pageHeightPxRef.current,
          sectionsReached: snapshot.filter((section) => section.reached).length,
          sectionsTotal: snapshot.length,
          tabsVisited: tabsVisitedRef.current.join(","),
        },
      };
    },
  });

  /** A tab, or a jump of the desktop rail: kept in order, once per visit. */
  const noteTab = useCallback((tab: string) => {
    if (tabsVisitedRef.current.at(-1) === tab) return;
    tabsVisitedRef.current.push(tab);
  }, []);

  const markConsumed = useCallback(() => {
    const currentArticle = articleRef.current;
    if (currentArticle === null) return;
    if (isConsumedRef.current) return;
    isConsumedRef.current = true;

    debug.log("Article consumed", { id: currentArticle.id });
    gaEventArticleConsumed({
      article: currentArticle,
      listName: AnalyticsItemList.ARTICLE_PAGE,
    });

    if (isAuthenticatedRef.current) {
      void apiClient.post("/webapp/private/articles/:id/reads", {
        param: { id: currentArticle.id },
      });
    }
    recordArticleRead(currentArticle.id);
  }, []);

  // Reset consumed state on article change
  const articleIdRef = useRef<string | null>(null);
  if (article !== null && article.id !== articleIdRef.current) {
    articleIdRef.current = article.id;
    isConsumedRef.current = false;
  }

  // 10-second timer: fires article_consumed if no earlier trigger
  useEffect(() => {
    if (article === null) return;
    const timer = setTimeout(markConsumed, CONSUMPTION_TIMER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [articleId, markConsumed]);

  // Scroll listener: any scroll marks article as consumed
  useEffect(() => {
    if (article === null) return;

    const anchor = scrollAnchorRef.current;
    const scrollContainer =
      anchor === null ? null : findScrollContainer(anchor);
    // No scrolling element above the anchor means the page scrolls the document,
    // so `window` is the right target there — and the wrong one everywhere else.
    const scrollTarget: HTMLElement | Window =
      scrollContainer === null ? window : scrollContainer;

    function handleScroll() {
      markConsumed();
      measureScroll();
    }

    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [articleId, markConsumed]);

  const flushViewDuration = useCallback(() => {
    if (viewedArticleRef.current === null || viewStartRef.current === null)
      return;
    const durationSeconds = Math.round(
      (Date.now() - viewStartRef.current) / 1000
    );
    trackContentViewDuration({
      contentId: viewedArticleRef.current.id,
      contentName: viewedArticleRef.current.title,
      contentType: "article",
      listName: AnalyticsItemList.ARTICLE_PAGE,
      durationSeconds,
      location: viewLocationRef.current,
      contentOrigin: viewOriginRef.current,
    });
    viewStartRef.current = null;
    viewedArticleRef.current = null;
  }, []);

  // Page view + view_item on slug change
  useEffect(() => {
    if (
      article !== null &&
      article.slug !== undefined &&
      article.slug !== memSlugRef.current
    ) {
      resetAnalytics(article);
      memSlugRef.current = article.slug;
    }
  }, [articleSlug]);

  // Flush view duration on unmount or tab close / app background
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        flushViewDuration();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      flushViewDuration();
    };
  }, [flushViewDuration]);

  // Mark article as read when read marker is on screen (legacy API call)
  useEffect(() => {
    if (!isArticleReadRef.current && isArticleReadMarkerOnScreen) {
      isArticleReadRef.current = true;
      markConsumed();
    }
  }, [isArticleReadMarkerOnScreen, markConsumed]);

  function resetAnalytics(article: ArticleAnalyticsModel) {
    try {
      debug.log("Reset analytics on slug change", { slug: article.slug });

      flushViewDuration();

      if (isAuthenticatedFirebase) {
        void apiClient.post("/webapp/private/articles/:id/views", {
          param: { id: article.id },
        });
      }
      gaEventPageView(currentRoom);
      gaEventViewItemArticle(
        {
          article,
          listName: AnalyticsItemList.ARTICLE_PAGE,
        },
        congressAttribution
      );

      viewStartRef.current = Date.now();
      viewedArticleRef.current = article;
      viewLocationRef.current = getContentLocation();
      const contentOrigin = getContentOrigin();
      viewOriginRef.current =
        contentOrigin === null ? "public app" : contentOrigin;

      isArticleReadRef.current = false;
      isSourceViewedRef.current = false;
    } catch (error) {
      console.error("Couldn't reset article analytics.", error);
    }
  }

  function handleViewArticleSource() {
    try {
      progress.noteExit("source_link");
      if (article && !isSourceViewedRef.current) {
        debug.log("Mark article source as viewed", {
          article,
          isSourceViewed: isSourceViewedRef.current,
        });
        if (isAuthenticatedFirebase) {
          void apiClient.post("/webapp/private/articles/:id/source-views", {
            param: { id: article.id },
          });
        }

        gaEventArticleViewOriginalPaper({
          article: article,
          listName: AnalyticsItemList.ARTICLE_PAGE,
        });
        isSourceViewedRef.current = true;
      }
    } catch (error) {
      console.error("Couldn't mark article source as viewed.", error);
    }
  }

  return {
    handleViewArticleSource,
    markConsumed,
    noteExit: progress.noteExit,
    noteTab,
  };
}
